'use client';

import { useState, useTransition } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { REVIEW_TAGS, type ReviewTag } from '@kit/marketplace/services/review';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Label } from '@kit/ui/label';
import { toast } from '@kit/ui/sonner';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { submitSellerReviewAction } from '../../_lib/server/submit-seller-review.action';
import { StarPicker } from './stars';

interface ReviewFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sellerId: string;
  sellerName: string;
  listingId?: string;
  /** Path to revalidate after posting (e.g. the current listing detail path). */
  revalidatePath?: string;
  /** Called after a successful post so the parent can refresh the list. */
  onSubmitted: () => void;
  /** Existing values when editing the reviewer's own review. */
  initial?: {
    rating: number;
    asDescribed: number;
    tags: ReviewTag[];
    body: string;
  };
}

export function ReviewFormDialog({
  open,
  onOpenChange,
  sellerId,
  sellerName,
  listingId,
  revalidatePath,
  onSubmitted,
  initial,
}: ReviewFormDialogProps) {
  const { t } = useTranslation();
  // State is initialized from `initial` once. The parent remounts this dialog
  // (via a `key` that flips on open) so each open starts fresh — no
  // reset-in-effect (react-hooks/set-state-in-effect).
  const [rating, setRating] = useState(initial?.rating ?? 0);
  const [asDescribed, setAsDescribed] = useState(initial?.asDescribed ?? 0);
  const [tags, setTags] = useState<ReviewTag[]>(initial?.tags ?? []);
  const [body, setBody] = useState(initial?.body ?? '');
  const [isPending, startTransition] = useTransition();

  const toggleTag = (tag: ReviewTag) =>
    setTags((cur) =>
      cur.includes(tag) ? cur.filter((t) => t !== tag) : [...cur, tag],
    );

  function handleSubmit() {
    if (rating < 1) return;
    startTransition(async () => {
      const result = await submitSellerReviewAction({
        sellerId,
        listingId,
        rating,
        asDescribed: asDescribed || undefined,
        tags,
        body: body.trim() || undefined,
        revalidate: revalidatePath,
      });
      if (result.status === 'ok') {
        toast.success(t('explore:reviewForm.posted'));
        onOpenChange(false);
        onSubmitted();
      } else {
        toast.error(result.error);
      }
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            <Trans
              i18nKey="explore:reviewForm.title"
              values={{ name: sellerName }}
            />
          </DialogTitle>
          <DialogDescription className="flex items-center gap-1.5">
            <ShieldCheck className="text-primary size-4" />
            <Trans i18nKey="explore:reviewForm.description" />
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-5 overflow-y-auto py-1">
          <div className="space-y-2">
            <Label>
              <Trans i18nKey="explore:reviewForm.overallRating" />
            </Label>
            <StarPicker
              value={rating}
              onChange={setRating}
              label={t('explore:reviewForm.overallRatingLabel')}
            />
          </div>

          <div className="space-y-2">
            <Label>
              <Trans i18nKey="explore:reviewForm.itemAsDescribed" />{' '}
              <span className="text-muted-foreground font-normal">
                <Trans i18nKey="explore:reviewForm.optional" />
              </span>
            </Label>
            <StarPicker
              value={asDescribed}
              onChange={setAsDescribed}
              size={22}
              label={t('explore:reviewForm.itemAsDescribedLabel')}
            />
          </div>

          <div className="space-y-2">
            <Label>
              <Trans i18nKey="explore:reviewForm.whatStoodOut" />{' '}
              <span className="text-muted-foreground font-normal">
                <Trans i18nKey="explore:reviewForm.optional" />
              </span>
            </Label>
            <div className="flex flex-wrap gap-2">
              {REVIEW_TAGS.map((tag) => {
                const active = tags.includes(tag);
                return (
                  // eslint-disable-next-line kit-components/use-kit-primitives -- zero-chrome interactive wrapper around a <Badge> pill; kit Button's h-9/px-4 chrome would break the tight tag-chip layout
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    aria-pressed={active}
                  >
                    <Badge
                      variant={active ? 'default' : 'outline'}
                      className={cn(
                        'cursor-pointer',
                        !active && 'hover:border-primary',
                      )}
                    >
                      {tag}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-body">
              <Trans i18nKey="explore:reviewForm.comment" />{' '}
              <span className="text-muted-foreground font-normal">
                <Trans i18nKey="explore:reviewForm.optional" />
              </span>
            </Label>
            <Textarea
              id="review-body"
              rows={3}
              value={body}
              maxLength={2000}
              placeholder={t('explore:reviewForm.commentPlaceholder')}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <Trans i18nKey="common:cancel" />
          </Button>
          <Button onClick={handleSubmit} disabled={rating < 1 || isPending}>
            {isPending
              ? t('explore:reviewForm.posting')
              : t('explore:reviewForm.postReview')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
